// src/screens/MarketingHub.js
// Friday-morning combined marketing dashboard. The one screen you look at every week.
//
// Pulls high-level numbers from multiple sources:
//   - public.user_acquisition (UTM-tagged signups, populated by app on signup)
//   - public.google_ads_campaigns (Google Ads API sync)
//   - public.seo_summary (Search Console sync)
//   - public.posthog_events_summary (PostHog API sync)
//
// Each section degrades gracefully if its underlying RPC isn't wired yet —
// shows "Connect [source]" inline rather than failing the whole page.

import React, { useState, useEffect } from 'react';
import { MetricCard, Card, CardTitle, Grid, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const fmt = (n) => (n == null ? '—' : n.toLocaleString());
const usd = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const pct = (n) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);

async function tryRpc(name, args = {}) {
  try {
    const { data, error } = await supabase.rpc(name, args);
    if (error) return { ok: false, error };
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

function MissingSourceCard({ source, instructions }) {
  return (
    <Card>
      <CardTitle>{source}: not connected</CardTitle>
      <div style={{ fontFamily: F, fontSize: 12, color: '#6F8FA7', padding: '8px 0', lineHeight: 1.5 }}>
        {instructions}
      </div>
    </Card>
  );
}

export default function MarketingHub() {
  const [weekly, setWeekly] = useState(null);
  const [topSources, setTopSources] = useState([]);
  const [seoOk, setSeoOk] = useState(null);
  const [adsOk, setAdsOk] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [w, s, seo, ads] = await Promise.all([
        tryRpc('admin_get_marketing_weekly'),
        tryRpc('admin_get_top_acquisition_sources', { limit: 5 }),
        tryRpc('admin_get_seo_summary'),
        tryRpc('admin_get_google_ads_summary'),
      ]);
      if (cancelled) return;
      if (w.ok) setWeekly(w.data);
      if (s.ok) setTopSources(s.data || []);
      setSeoOk(seo.ok);
      setAdsOk(ads.ok);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ fontFamily: F, padding: 20 }}>
      <h1 style={{ fontSize: 24, fontWeight: 500, color: '#163040', margin: '0 0 4px' }}>Marketing</h1>
      <p style={{ color: '#6F8FA7', fontSize: 13, margin: '0 0 24px' }}>
        Friday morning at a glance. Last 7 days vs prior 7.
      </p>

      <Grid>
        <MetricCard label="New paying members" value={fmt(weekly?.new_members)} delta={weekly?.new_members_delta} />
        <MetricCard label="Blended CAC" value={usd(weekly?.blended_cac)}
          status={weekly?.blended_cac > 15 ? 'warning' : 'ok'}
          delta={weekly?.blended_cac_delta ? -weekly.blended_cac_delta : null} />
        <MetricCard label="Web → signup CR" value={pct(weekly?.signup_cr)} delta={weekly?.signup_cr_delta} />
        <MetricCard label="Signup → Passage CR" value={pct(weekly?.passage_cr)} delta={weekly?.passage_cr_delta} />
        <MetricCard label="Founding spots left" value={fmt(weekly?.founding_spots_left)} />
        <MetricCard label="MRR change" value={usd(weekly?.mrr_delta)} delta={weekly?.mrr_delta} />
      </Grid>

      <Card style={{ marginTop: 20 }}>
        <CardTitle>Top 5 acquisition sources · last 7 days</CardTitle>
        {topSources.length === 0 ? (
          <div style={{ fontSize: 13, color: '#6F8FA7', padding: 12 }}>
            No UTM-tagged signups yet. Make sure every campaign link includes <code>utm_source</code>,
            <code> utm_medium</code>, <code>utm_campaign</code>. The <code>UTM</code> screen has the link builder.
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: 12, marginTop: 8, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6F8FA7', borderBottom: '.5px solid #d0dde7' }}>
                <th style={{ padding: '6px 8px' }}>Source</th>
                <th style={{ padding: '6px 8px' }}>Medium</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Signups</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Spend</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>CAC</th>
              </tr>
            </thead>
            <tbody>
              {topSources.map((s, i) => (
                <tr key={i} style={{ borderBottom: '.5px solid #edf2f5' }}>
                  <td style={{ padding: '8px', color: '#163040' }}>{s.utm_source || 'direct'}</td>
                  <td style={{ padding: '8px' }}>{s.utm_medium || '—'}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(s.signups)}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{s.spend ? usd(s.spend) : '—'}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{s.cac ? usd(s.cac) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginTop: 20 }}>
        {seoOk === false && (
          <MissingSourceCard
            source="Search Console"
            instructions={<>Open <a href="#" onClick={(e) => { e.preventDefault(); window.sanoireNav?.('seo'); }}>SEO</a> to wire up the GSC pipeline.</>}
          />
        )}
        {adsOk === false && (
          <MissingSourceCard
            source="Google Ads"
            instructions={<>Open <a href="#" onClick={(e) => { e.preventDefault(); window.sanoireNav?.('googleads'); }}>Google Ads</a> to wire up the Ads API pipeline.</>}
          />
        )}
      </div>

      <Insight title="The Friday Morning Ritual">
        Spend 30 minutes here. If blended CAC is above $15 or signup→Passage CR drops below 30%,
        that's the week's priority. Drill into UTM Referral or the SEO/Ads screens for root cause.
      </Insight>
    </div>
  );
}
